import Tribute, { TributeItem } from "c8-tributejs";
import { cnl } from "csi/typings";
import { ILocaleProvider } from "designSystem/locale";
import { UserQuery } from "designSystem/user";
import { debounce } from "lodash";
import { LegacyRef } from "react";
import FroalaEditor from "react-froala-wysiwyg";
import { UserSearchCallback } from "./useMentionUser";

const START_TRIGGER_SEARCH_CHAR_LENGTH = 0;
export const MENTION_USER_URL = "mentionuserurl";
export const AT_TAG = "at";

type UserSearchStateCallback = (searchUser: UserQuery) => void;
type UserCallback = (fun: UserSearchCallback) => void
type TemplateCallback = (user: TributeItem<MentionUser>) => string;

export interface MentionUser {
	id: number;
	url: cnl;
	firstName: string;
	lastName: string;
	fullName: string;
	avatar: string;
	email: string;

	//lookup for tribute to send both name and email (lookup = name + ' ' + email)
	lookup: string;
}

/**
 * Return mentioned user list from html - parse the html string with Anchor tag
 */
export function getMentionedUsers(htmlString: string): string[] {
	let mentions = [];
	const el = document.createElement("html");
	el.innerHTML = htmlString;
	const nodeList: HTMLCollectionOf<HTMLSpanElement> = el.getElementsByTagName("span");

	if (nodeList) {
		mentions = Array.from(nodeList)
			.filter((node) => node.hasAttribute(MENTION_USER_URL))
			.map((node) => node.getAttribute(MENTION_USER_URL));
	}
	return [...new Set(mentions)];
}

const defaultAvatar = `<svg class="avatar" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<circle cx="12" cy="12" r="12" fill="#CED4DE"/>
			<path fill-rule="evenodd" clip-rule="evenodd" d="M11.9999 11C13.6568 11 14.9999 9.65685 14.9999 8C14.9999 6.34315 13.6568 5 11.9999 5C10.3431 5 8.99992 6.34315 8.99992 8C8.99992 9.65685 10.3431 11 11.9999 11ZM11.9999 12C8.40894 12 5.4495 14.704 5.04663 18.1869C6.84621 19.9284 9.29791 21 11.9999 21C14.7019 21 17.1536 19.9284 18.9532 18.1869C18.5503 14.704 15.5909 12 11.9999 12Z" fill="#525866"/>
			</svg>`;

/**
 * Define tribute start search trigger logic and set call for mention hook
 */
const tributeMapping = (ref: LegacyRef<FroalaEditor>, setUserSearchState: UserSearchStateCallback, setUserCallback: UserCallback, selectTemplate: TemplateCallback, locale: ILocaleProvider): void => {
	const editor = ref["current"]["editor"];
	const handleTextChange = debounce((query: UserQuery) => {
		setUserSearchState(query);
	}, 200);

	const tribute = new Tribute<MentionUser>({
		values: (enterText: string, callback: UserSearchCallback): void => {
			if (enterText.length >= START_TRIGGER_SEARCH_CHAR_LENGTH) {
				setUserCallback(() => callback);
				const query: UserQuery = { input: enterText };
				handleTextChange(query);
			}
		},
		lookup: (user: MentionUser) => user.lookup,
		fillAttr: "fullName",
		allowSpaces: true,
		loadingItemTemplate: `<div style="padding: 16px">${locale.resource("Loading")}</div>`,
		noMatchTemplate: (): string => {
			return "<span style:\"visibility: hidden;\"></span>";
		},
		menuItemTemplate: (user: TributeItem<MentionUser>): string => {
			const avatar = user.original.avatar ? `
			<span class="MuiAvatar-root MuiAvatar-circular avatar"><img class="MuiAvatar-img" src="${user.original.avatar}"></span>` : defaultAvatar;
			return `<div>${avatar}<span title="${user.original.fullName}">${user.original.fullName}</span></div>`;
		},
		selectTemplate,
	});
	tribute.attach(editor.el);
	editor.events.on("keydown", (e: any) => {
		if (tribute.isActive) {
			setUserSearchState(null);
			return false;
		}
	}, true);
};

export default tributeMapping;